import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowUpDown,
  Clock,
  Copy,
  Globe2,
  FileText,
  Mail,
  MapPin,
  Pencil,
  Phone,
  Plus,
  Ruler,
  Trash2,
  User,
  Warehouse,
} from "lucide-react";
import BayForm from "@/components/BayForm";
import BulkActionsBar from "@/components/BulkActionsBar";
import { Checkbox } from "@/components/ui/checkbox";
import SiteForm from "@/components/SiteForm";
import { BAY_DEFAULTS } from "@/lib/constants";
import { bulkDeleteBays, bulkSetBayActive, deleteBay, getSite, listBays, resequenceBays } from "@/lib/api";

export default function SiteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [site, setSite] = useState(null);
  const [bays, setBays] = useState([]);
  const [loading, setLoading] = useState(true);

  const [editingSite, setEditingSite] = useState(false);
  const [editingBay, setEditingBay] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmResequence, setConfirmResequence] = useState(false);
  const [resequencing, setResequencing] = useState(false);

  // Bulk selection on bays
  const [selectedBayIds, setSelectedBayIds] = useState(new Set());

  // Open the bay form pre-filled from an existing bay. Strips id /
  // timestamps so the form treats it as INSERT, and suffixes name + code
  // so unique constraints don't trip.
  const startDuplicateBay = (b) => {
    const { id, created_at, updated_at, ...rest } = b;
    setEditingBay({
      ...rest,
      name: `${b.name} (copy)`,
      code: b.code ? `${b.code}-COPY` : "",
    });
  };
  const toggleOne = (id) => setSelectedBayIds((s) => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleAll = () => setSelectedBayIds((s) => {
    if (s.size === bays.length) return new Set();
    return new Set(bays.map((b) => b.id));
  });
  const clearSel = () => setSelectedBayIds(new Set());

  const bulkBayActive = async (active) => {
    const ids = Array.from(selectedBayIds);
    const { error } = await bulkSetBayActive(ids, active);
    if (error) { toast.error(error.message); return; }
    toast.success(`${ids.length} bays → ${active ? "active" : "inactive"}.`);
    clearSel();
    fetchAll();
  };
  const bulkBayDelete = async () => {
    if (!window.confirm(`Delete ${selectedBayIds.size} bay(s)? This will also delete their bookings.`)) return;
    const ids = Array.from(selectedBayIds);
    const { error } = await bulkDeleteBays(ids);
    if (error) { toast.error(error.message); return; }
    toast.success(`Deleted ${ids.length} bays.`);
    clearSel();
    fetchAll();
  };

  const runResequence = async () => {
    setResequencing(true);
    const { count, error } = await resequenceBays(id);
    setResequencing(false);
    setConfirmResequence(false);
    if (error) { toast.error(error.message); return; }
    if (count === 0) { toast.success("Already in natural name order — nothing to do."); return; }
    toast.success(`Re-sequenced ${count} bay${count === 1 ? "" : "s"}.`);
    fetchAll();
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const [{ data: s, error: e1 }, { data: b, error: e2 }] = await Promise.all([
      getSite(id),
      listBays(id),
    ]);
    if (e1 || e2) toast.error(e1?.message || e2?.message);
    setSite(s);
    setBays(b || []);
    setLoading(false);
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [{ data: s, error: e1 }, { data: b, error: e2 }] = await Promise.all([
        getSite(id),
        listBays(id),
      ]);
      if (cancelled) return;
      if (e1 || e2) toast.error(e1?.message || e2?.message);
      setSite(s);
      setBays(b || []);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const nextSortOrder = useMemo(() => {
    if (bays.length === 0) return 1;
    return Math.max(...bays.map((b) => b.sort_order ?? 0)) + 1;
  }, [bays]);

  const handleDeleteBay = async () => {
    if (!confirmDelete) return;
    const { error } = await deleteBay(confirmDelete.id);
    setConfirmDelete(null);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Bay deleted");
    fetchAll();
  };

  if (loading && !site) {
    return (
      <div data-testid="site-detail-loading" className="text-sm text-slate-500">
        Loading site…
      </div>
    );
  }

  if (!site) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={() => navigate("/sites")}>
          <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to sites
        </Button>
        <Card>
          <CardContent className="pt-6 text-sm text-slate-500">
            Site not found, or you don&apos;t have access.
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div data-testid="site-detail-page" className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/sites")}
            className="-ml-3 mb-1"
            data-testid="back-to-sites-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-1.5" /> Sites
          </Button>
          <h1 className="text-2xl font-semibold tracking-tight">
            {site.name}
            {site.code && (
              <span className="ml-2 text-base text-slate-500 font-normal tabular-nums">
                · {site.code}
              </span>
            )}
            {!site.is_active && (
              <Badge variant="outline" className="ml-3 align-middle">Inactive</Badge>
            )}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <Link to={`/sites/${id}/rules`}>
            <Button variant="outline" data-testid="site-rules-btn">
              <FileText className="h-4 w-4 mr-1.5" /> Site rules
            </Button>
          </Link>
          <Button variant="outline" onClick={() => setEditingSite(true)} data-testid="edit-site-btn">
            <Pencil className="h-4 w-4 mr-1.5" /> Edit site
          </Button>
        </div>
      </div>

      {/* Site detail cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <InfoCard icon={MapPin} title="Address">
          {[site.address_line1, site.address_line2, [site.city, site.state, site.postcode].filter(Boolean).join(" ")]
            .filter(Boolean)
            .map((line, i) => (
              <div key={i} className="text-sm">{line}</div>
            ))}
          {!site.address_line1 && !site.city && (
            <div className="text-sm text-slate-400">No address set</div>
          )}
          <div className="flex items-center gap-1.5 text-xs text-slate-500 mt-2">
            <Globe2 className="h-3.5 w-3.5" /> {site.timezone}
          </div>
        </InfoCard>

        <InfoCard icon={Clock} title="Operating hours">
          <div className="text-sm">
            <span className="tabular-nums">{site.operating_hours_start?.slice(0, 5)}</span>
            <span className="text-slate-400 mx-1.5">→</span>
            <span className="tabular-nums">{site.operating_hours_end?.slice(0, 5)}</span>
          </div>
          <div className="text-xs text-slate-500 mt-1">
            {site.slot_duration_minutes}-minute slots
          </div>
        </InfoCard>

        <InfoCard icon={User} title="Contact">
          {site.contact_name && <div className="text-sm font-medium">{site.contact_name}</div>}
          {site.contact_email && (
            <div className="flex items-center gap-1.5 text-xs text-slate-600 mt-1">
              <Mail className="h-3.5 w-3.5" /> {site.contact_email}
            </div>
          )}
          {site.contact_phone && (
            <div className="flex items-center gap-1.5 text-xs text-slate-600 mt-1">
              <Phone className="h-3.5 w-3.5" /> {site.contact_phone}
            </div>
          )}
          {!site.contact_name && !site.contact_email && !site.contact_phone && (
            <div className="text-sm text-slate-400">No contact set</div>
          )}
        </InfoCard>
      </div>

      {/* Bays */}
      <BulkActionsBar
        selectedCount={selectedBayIds.size}
        onClear={clearSel}
        actions={[
          { key: "activate",   label: "Set active",   onClick: () => bulkBayActive(true) },
          { key: "deactivate", label: "Set inactive", onClick: () => bulkBayActive(false), variant: "outline" },
          { key: "delete",     label: "Delete",       onClick: bulkBayDelete,               variant: "destructive" },
        ]}
      />
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-3 py-3 space-y-0">
          <CardTitle className="text-base">
            Loading bays
            <span className="ml-2 text-sm font-normal text-slate-500 tabular-nums">
              ({bays.length})
            </span>
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setConfirmResequence(true)}
              disabled={bays.length < 2}
              data-testid="resequence-bays-btn"
              title="Renumber sort_order so bays list in natural name order (Bay 1, 2, 3… 10)"
            >
              <ArrowUpDown className="h-4 w-4 mr-1.5" /> Re-sequence
            </Button>
            <Button size="sm" onClick={() => setEditingBay({ __new: true })} data-testid="add-bay-btn">
              <Plus className="h-4 w-4 mr-1.5" /> Add bay
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {bays.length === 0 ? (
            <BaysEmptyState onAdd={() => setEditingBay({ __new: true })} />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8">
                      <Checkbox
                        checked={selectedBayIds.size > 0 && selectedBayIds.size === bays.length}
                        onCheckedChange={toggleAll}
                        data-testid="bays-select-all"
                        aria-label="Select all bays"
                      />
                    </TableHead>
                    <TableHead className="w-1">&nbsp;</TableHead>
                    <TableHead>Bay</TableHead>
                    <TableHead>Code</TableHead>
                    <TableHead>Approval</TableHead>
                    <TableHead>Max duration</TableHead>
                    <TableHead>Vehicle limits</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-12 text-right">&nbsp;</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bays.map((b) => (
                    <TableRow key={b.id} data-testid={`bay-row-${b.id}`}>
                      <TableCell className="w-8">
                        <Checkbox
                          checked={selectedBayIds.has(b.id)}
                          onCheckedChange={() => toggleOne(b.id)}
                          data-testid={`bays-select-${b.id}`}
                          aria-label={`Select bay ${b.name}`}
                        />
                      </TableCell>
                      <TableCell>
                        <span
                          className="inline-block h-3 w-3 rounded-full"
                          style={{ backgroundColor: b.color || BAY_DEFAULTS.color }}
                          title={b.color}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="font-medium text-sm">{b.name}</div>
                        {b.description && (
                          <div className="text-xs text-slate-500 max-w-xs truncate">
                            {b.description}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-sm tabular-nums">{b.code || "—"}</TableCell>
                      <TableCell>
                        {b.requires_approval ? (
                          <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-800">
                            Required
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700">
                            Auto
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm tabular-nums">
                        {b.max_booking_hours} h
                      </TableCell>
                      <TableCell className="text-xs text-slate-600">
                        {formatVehicleLimits(b)}
                      </TableCell>
                      <TableCell>
                        {b.is_active ? (
                          <Badge variant="secondary" className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                            Active
                          </Badge>
                        ) : (
                          <Badge variant="outline">Inactive</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="inline-flex items-center gap-0.5">
                          <Button variant="ghost" size="icon" className="h-8 w-8" title="Edit bay"
                                  onClick={() => setEditingBay(b)} data-testid={`bay-edit-${b.id}`}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8" title="Duplicate bay"
                                  onClick={() => startDuplicateBay(b)} data-testid={`bay-duplicate-${b.id}`}>
                            <Copy className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50" title="Delete bay"
                                  onClick={() => setConfirmDelete(b)} data-testid={`bay-delete-${b.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <SiteForm
        open={editingSite}
        onOpenChange={setEditingSite}
        site={site}
        onSaved={fetchAll}
      />

      <BayForm
        open={!!editingBay}
        onOpenChange={(open) => !open && setEditingBay(null)}
        siteId={site.id}
        site={site}
        bay={editingBay?.__new ? null : editingBay}
        nextSortOrder={nextSortOrder}
        onSaved={fetchAll}
      />

      <AlertDialog open={!!confirmDelete} onOpenChange={(open) => !open && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete bay?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete <strong>{confirmDelete?.name}</strong>. Any bookings tied
              to this bay will also be removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-delete-bay-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteBay}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-bay"
            >
              Yes, delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmResequence} onOpenChange={(open) => !resequencing && setConfirmResequence(open)}>
        <AlertDialogContent data-testid="resequence-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Re-sequence bays?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>
                  This will renumber <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">sort_order</code> for
                  all {bays.length} bays so they display in natural name order &mdash; &ldquo;Bay 1, Bay 2, Bay 3 &hellip; Bay 10, Bay 20&rdquo;
                  instead of creation order.
                </p>
                <div className="text-xs text-slate-600 border-l-2 border-slate-300 pl-3">
                  <div className="font-medium text-slate-700 mb-1">New order:</div>
                  <div className="tabular-nums leading-relaxed">
                    {[...bays]
                      .sort((a, b) =>
                        new Intl.Collator(undefined, { numeric: true, sensitivity: "base" })
                          .compare(a?.name || "", b?.name || "")
                      )
                      .slice(0, 12)
                      .map((b, i) => (
                        <div key={b.id}>
                          <span className="text-slate-400 mr-2">{i + 1}.</span>
                          {b.name}
                        </div>
                      ))}
                    {bays.length > 12 && (
                      <div className="text-slate-400 italic mt-1">…and {bays.length - 12} more</div>
                    )}
                  </div>
                </div>
                <p className="text-xs text-slate-500">
                  Booking data is not affected. You can undo by editing individual bays and adjusting sort_order manually.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={resequencing} data-testid="resequence-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={runResequence}
              disabled={resequencing}
              data-testid="resequence-confirm"
            >
              {resequencing ? "Re-sequencing…" : "Re-sequence"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function InfoCard({ icon: Icon, title, children }) {
  return (
    <Card>
      <CardContent className="pt-5">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 mb-2">
          <Icon className="h-3.5 w-3.5" /> {title}
        </div>
        {children}
      </CardContent>
    </Card>
  );
}

function BaysEmptyState({ onAdd }) {
  return (
    <div className="py-10 text-center" data-testid="bays-empty-state">
      <div className="mx-auto h-12 w-12 rounded-full bg-slate-100 flex items-center justify-center mb-3">
        <Warehouse className="h-6 w-6 text-slate-400" />
      </div>
      <h3 className="text-sm font-medium">No bays yet</h3>
      <p className="text-sm text-slate-500 mt-1">Add your first bay to start scheduling bookings here.</p>
      <Button size="sm" className="mt-4" onClick={onAdd} data-testid="empty-add-bay-btn">
        <Plus className="h-4 w-4 mr-1.5" /> Add bay
      </Button>
    </div>
  );
}

function formatVehicleLimits(b) {
  const parts = [];
  if (b.max_vehicle_length) parts.push(`L ${b.max_vehicle_length}m`);
  if (b.max_vehicle_height) parts.push(`H ${b.max_vehicle_height}m`);
  if (b.max_vehicle_weight) parts.push(`GVM ${b.max_vehicle_weight}kg`);
  return parts.length > 0 ? parts.join(" · ") : <Ruler className="h-3.5 w-3.5 text-slate-300" />;
}
